/**
 * Bypass-aware re-exports of Clerk's client hooks/components.
 *
 * In normal mode these forward to `@clerk/nextjs`. With
 * NEXT_PUBLIC_DEV_AUTH_BYPASS=true (dev only) they return a fake
 * signed-in state and do not call Clerk — so pages render without
 * requiring `<ClerkProvider>` in the tree.
 *
 * Import from here instead of `@clerk/nextjs` for `useUser`, `useAuth`,
 * `useClerk`, `Show`, `SignInButton`, `SignUpButton`, `UserAvatar`, and `UserButton`
 * in any module that needs to work under bypass.
 */

import { useNavigate } from 'react-router-dom';

import Link from '@/lib/nav/link';
import { Children, cloneElement, isValidElement, useMemo } from 'react';
import type { ComponentProps, MouseEvent, ReactElement, ReactNode } from 'react';
import * as Clerk from '@clerk/react';
import { isDevAuthBypassEnabled } from '@/lib/dev/auth-bypass-flag';
import { DEV_PREVIEW_USER_ID, devPreviewUser } from './dev-fake-user';

const FAKE_SESSION_ID = 'dev-preview-session';

const fakeUserState = {
  isLoaded: true,
  isSignedIn: true,
  user: devPreviewUser,
} as const;

// Shape kept loose to satisfy whatever Clerk/Convex consumers expect
// from `useAuth()`. The bypass return value is type-asserted at the
// hook boundary so we don't have to track Clerk's full UseAuth shape.
const fakeAuthState = {
  isLoaded: true,
  isSignedIn: true,
  userId: DEV_PREVIEW_USER_ID,
  sessionId: FAKE_SESSION_ID,
  sessionClaims: {
    sub: DEV_PREVIEW_USER_ID,
    email: 'dev-preview@example.test',
  } as Record<string, unknown>,
  actor: null,
  orgId: null,
  orgRole: null,
  orgSlug: null,
  orgPermissions: null,
  factorVerificationAge: null,
  getToken: async () => null,
  signOut: async () => {},
  has: () => false,
};

export function useUser() {
  // The bypass flag is a build-time constant — the conditional below
  // always resolves the same way at runtime, so React's hook rules
  // are not violated.
  if (isDevAuthBypassEnabled) return fakeUserState;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return Clerk.useUser();
}

export function useAuth(): ReturnType<typeof Clerk.useAuth> {
  if (isDevAuthBypassEnabled) {
    return fakeAuthState as unknown as ReturnType<typeof Clerk.useAuth>;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return Clerk.useAuth();
}

export function useClerk() {
  if (isDevAuthBypassEnabled) {
    // Memoized stub object — methods are no-ops because Clerk isn't loaded.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
      () => ({
        user: devPreviewUser,
        session: { id: FAKE_SESSION_ID, status: 'active' },
        loaded: true,
        signOut: async () => {},
        openSignIn: () => {},
        openSignUp: () => {},
        openUserProfile: () => {},
      }),
      [],
    );
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return Clerk.useClerk();
}

export function Show({
  when,
  children,
  fallback,
}: {
  when?: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  if (isDevAuthBypassEnabled) {
    if (when === 'signed-out') return <>{fallback ?? null}</>;
    return <>{children}</>;
  }
  return (
    <Clerk.Show when={when as never} fallback={fallback}>
      {children}
    </Clerk.Show>
  );
}

type ButtonProps = {
  children?: ReactNode;
  /**
   * Accepted for API compatibility with Clerk's original buttons.
   * We always navigate to the dedicated auth page — no modal — so
   * this prop is ignored.
   */
  mode?: 'modal' | 'redirect';
  forceRedirectUrl?: string;
  fallbackRedirectUrl?: string;
};

function buildAuthHref(path: '/signup' | '/login', next: string | undefined) {
  if (!next) return path;
  return `${path}?next=${encodeURIComponent(next)}`;
}

/**
 * Wraps `children` so that clicking them routes to the dedicated
 * `/signup` or `/login` page (matching the marketing CTA pattern)
 * instead of opening Clerk's modal. The child is expected to be a
 * single interactive element (typically a `<button>`); its existing
 * onClick is preserved and chained.
 */
function NavigateOnClick({ href, children }: { href: string; children: ReactNode }) {
  // A full document load here would tear down the Convex WebSocket and
  // re-download the bundle on every auth click. Route in-app instead.
  const navigate = useNavigate();
  const onlyChild = Children.toArray(children).find((node) => isValidElement(node)) as
    | ReactElement<{ onClick?: (event: MouseEvent<HTMLElement>) => void }>
    | undefined;
  if (!onlyChild) {
    return (
      <Link href={href} className="inline-flex items-center">
        {children}
      </Link>
    );
  }
  const existingOnClick = onlyChild.props.onClick;
  return cloneElement(onlyChild, {
    onClick: (event: MouseEvent<HTMLElement>) => {
      existingOnClick?.(event);
      if (event.defaultPrevented) return;
      event.preventDefault();
      navigate(href);
    },
  });
}

export function SignInButton({ children, forceRedirectUrl, fallbackRedirectUrl }: ButtonProps) {
  if (isDevAuthBypassEnabled) return <>{children ?? null}</>;
  const href = buildAuthHref('/login', forceRedirectUrl ?? fallbackRedirectUrl);
  return <NavigateOnClick href={href}>{children}</NavigateOnClick>;
}

export function SignUpButton({ children, forceRedirectUrl, fallbackRedirectUrl }: ButtonProps) {
  if (isDevAuthBypassEnabled) return <>{children ?? null}</>;
  const href = buildAuthHref('/signup', forceRedirectUrl ?? fallbackRedirectUrl);
  return <NavigateOnClick href={href}>{children}</NavigateOnClick>;
}

type UserButtonProps = ComponentProps<typeof Clerk.UserButton>;
type UserAvatarProps = ComponentProps<typeof Clerk.UserAvatar>;

/**
 * Display-only account avatar. Unlike UserButton, this never owns a menu or
 * click target, so a surrounding link can provide one consistent action.
 */
export function UserAvatar({ appearance, fallback, ...props }: UserAvatarProps) {
  if (isDevAuthBypassEnabled) {
    return <>{fallback ?? null}</>;
  }
  return (
    <Clerk.UserAvatar
      {...props}
      rounded
      fallback={fallback}
      appearance={{
        ...appearance,
        elements: {
          rootBox: 'h-9 w-9 overflow-hidden rounded-full',
          userAvatarBox: 'h-9 w-9 overflow-hidden rounded-full',
          userAvatarImage: 'h-full w-full object-cover object-center',
          ...appearance?.elements,
        },
      }}
    />
  );
}

/**
 * Sizes the avatar to match the dev-bypass placeholder (h-9 w-9) and gives
 * the trigger a branded focus ring. The full-width white-card styling that
 * used to leak onto this button via the global `rootBox` now lives only on
 * the auth widgets (see `authWidgetAppearance`), so no reset is needed here.
 */
export function UserButton({ appearance, ...props }: UserButtonProps) {
  if (isDevAuthBypassEnabled) {
    return (
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/10 text-xs font-bold text-black/55"
        title="Dev bypass user"
        aria-label="Dev bypass user"
      >
        PU
      </span>
    );
  }
  return (
    <Clerk.UserButton
      {...props}
      appearance={{
        ...appearance,
        elements: {
          userButtonTrigger:
            'rounded-full focus:shadow-none focus-visible:ring-2 focus-visible:ring-coral/30',
          userButtonAvatarBox: 'h-9 w-9',
          ...appearance?.elements,
        },
      }}
    />
  );
}
